import { useCallback, useMemo, useState, useEffect } from 'react';
import {
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { receivedDemandsService, type DemandaFilters } from '../../../../services/receivedDemands';
import type { Demanda } from '../../../../models';
import type { DemandType } from '../../components/navigation';

export type DemandCacheKey = 'all' | 'received' | 'sent' | 'watching' | 'toAssign';

export const getDemandTypeCacheKey = (type: DemandType): DemandCacheKey => {
  switch (type) {
    case 'received':
      return 'received';
    case 'sent':
      return 'sent';
    case 'watching':
      return 'watching';
    case 'toAssign':
      return 'toAssign';
    case 'authorization':
      // Autorizações usam o mesmo cache que recebidas (filtramos por status depois)
      return 'received';
    default:
      return 'all';
  }
};

/**
 * Cria fetchers com suporte a filtros server-side
 */
const createDemandFetcher = (cacheKey: DemandCacheKey) => {
  return async (forceRefresh: boolean = false, filters?: DemandaFilters): Promise<Demanda[]> => {
    switch (cacheKey) {
      case 'all':
        return receivedDemandsService.getDemandas(forceRefresh, filters);
      case 'received':
        return receivedDemandsService.getDemandasRecebidas(forceRefresh, filters);
      case 'sent':
        return receivedDemandsService.getDemandasEnviadas(forceRefresh, filters);
      case 'watching':
        return receivedDemandsService.getDemandasObservaveis(forceRefresh, filters);
      case 'toAssign':
        return receivedDemandsService.getDemandasEmAtribuicao(forceRefresh, filters);
      default:
        return receivedDemandsService.getDemandas(forceRefresh, filters);
    }
  };
};

/**
 * Gera chave de cache única considerando os filtros
 */
export const demandasQueryKey = (cacheKey: DemandCacheKey, filters?: DemandaFilters): QueryKey => {
  const baseKey = ['unified-demandas', cacheKey];
  
  // Se não há filtros, retorna apenas a chave base
  if (!filters) return baseKey;
  
  // Adiciona filtros à chave para diferenciar queries
  const filterKey: Record<string, string | number | undefined> = {};
  if (filters.buscar) filterKey.buscar = filters.buscar;
  if (filters.status) filterKey.status = filters.status;
  if (filters.prioridade) filterKey.prioridade = filters.prioridade;
  if (filters.processo_fk) filterKey.processo_fk = filters.processo_fk;
  
  // Só adiciona filtros se houver algum ativo
  if (Object.keys(filterKey).length > 0) {
    return [...baseKey, filterKey];
  }
  
  return baseKey;
};

interface UseDemandasQueryOptions {
  /** Permite ajustar o staleness padrão do TanStack Query caso necessário. */
  staleTime?: number;
  /** Habilita polling adaptativo (mais rápido após mutações, mais lento depois). */
  enableAdaptivePolling?: boolean;
  /** Permite desabilitar a query condicionalmente */
  enabled?: boolean;
  /** Filtros server-side para a busca de demandas */
  filters?: DemandaFilters;
}

export const useDemandasQuery = (
  type: DemandType,
  { staleTime = 60 * 1000, enableAdaptivePolling = true, enabled = true, filters }: UseDemandasQueryOptions = {}
) => {
  const queryClient = useQueryClient();
  const cacheKey = useMemo(() => getDemandTypeCacheKey(type), [type]);
  const queryKey = useMemo(() => demandasQueryKey(cacheKey, filters), [cacheKey, filters]);
  
  // Criar fetcher com suporte a filtros
  const fetcher = useMemo(() => createDemandFetcher(cacheKey), [cacheKey]);

  // Polling adaptativo: acelera após mutações, volta ao normal depois
  const [pollingInterval, setPollingInterval] = useState<number | false>(
    enableAdaptivePolling ? 30000 : false // 30s padrão quando habilitado
  );

  // Listener para acelerar polling quando ocorrem mutações
  useEffect(() => {
    if (!enableAdaptivePolling) return;

    const handleMutation = () => {
      setPollingInterval(5000); // 5s - polling rápido
      
      // Voltar ao normal após 2 minutos
      const timer = setTimeout(() => {
        setPollingInterval(30000); // 30s - polling normal
      }, 2 * 60 * 1000);

      return () => clearTimeout(timer);
    };

    window.addEventListener('demand-mutation-occurred', handleMutation as any);
    return () => {
      window.removeEventListener('demand-mutation-occurred', handleMutation as any);
    };
  }, [enableAdaptivePolling]);

  const query = useQuery<Demanda[]>({
    queryKey,
    queryFn: () => fetcher(false, filters),
    staleTime,
    gcTime: 5 * 60 * 1000,
    retry: 1,
    enabled, // Controle de habilitação
    refetchInterval: pollingInterval, // Polling adaptativo
    refetchIntervalInBackground: true, // Continua polling em background
    refetchOnWindowFocus: true, // Refetch ao voltar para a aba
  });

  const forceRefresh = useCallback(async () => {
    const freshData = await fetcher(true, filters);
    queryClient.setQueryData(queryKey, freshData);
    return freshData;
  }, [fetcher, filters, queryClient, queryKey]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return {
    ...query,
    cacheKey,
    queryKey,
    forceRefresh,
    invalidate,
  } as const;
};
